import { defineStore } from 'pinia'
import router from '@/router'
import axios from 'axios'
import * as Sentry from '@sentry/vue'

const baseUrl = import.meta.env.VITE_YW_API_URL + '/auth'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    userToken: JSON.parse(localStorage.getItem('userToken')),
    tokenExpiry: JSON.parse(localStorage.getItem('tokenExpiry')),
    isLoggingOut: false,
    userPermissions: []
  }),
  actions: {
    async fetchUserPermissions() {
      try {
        const response = await axios.get(`${baseUrl}/permissions`, {
          headers: {
            Authorization: `Bearer ${this.userToken}`
          }
        })
        this.userPermissions = response.data || []
        return this.userPermissions
      } catch (error) {
        console.error('Error fetching permissions:', error)
        throw error
      }
    },

    hasAdminOrBrokerRole() {
      return (
        this.userPermissions.includes('role:admin') || this.userPermissions.includes('role:broker')
      )
    },

    isBroker() {
      return (
        this.userPermissions.includes('role:broker') && !this.userPermissions.includes('role:admin')
      )
    },

    isAdmin() {
      return this.userPermissions.includes('role:admin')
    },

    async login(username, password) {
      try {
        const response = await axios.post(`${baseUrl}/login`, {
          email: username,
          password: password
        })

        const tempToken = response.data.access_token || ''
        const expiresIn = response.data.expires_in || 3600
        const tempTokenExpiry = Date.now() + expiresIn * 1000

        // Set the Authorization header temporarily to fetch permissions
        axios.defaults.headers.common['Authorization'] = `Bearer ${tempToken}`

        // Fetch user permissions to verify access BEFORE setting the store state
        try {
          // Temporarily use the token to fetch permissions
          const permissionsResponse = await axios.get(`${baseUrl}/permissions`)
          const permissions = permissionsResponse.data || []

          // Refined access:
          // - admins always access admin panel
          // - brokers must also have application:all:insurance right
          const hasAccess =
            permissions.includes('role:admin') ||
            (permissions.includes('role:broker') &&
              permissions.includes('application:all:insurance'))

          if (!hasAccess) {
            // Clear the temporary authorization header
            delete axios.defaults.headers.common['Authorization']

            throw new Error('Access denied')
          }

          // Only set the token and permissions in store AFTER access is verified
          this.userToken = tempToken
          this.tokenExpiry = tempTokenExpiry
          this.userPermissions = permissions

          localStorage.setItem('userToken', JSON.stringify(this.userToken))
          localStorage.setItem('tokenExpiry', JSON.stringify(this.tokenExpiry))

          // Wait for next tick to ensure everything is set
          await new Promise((resolve) => setTimeout(resolve, 50))

          // Redirect to originally requested URL or home page
          const redirectPath = localStorage.getItem('redirectAfterLogin')
          if (redirectPath) {
            localStorage.removeItem('redirectAfterLogin')
            await router.push(redirectPath)
          } else {
            // Redirect brokers to insurance applications, admins to dashboard
            if (this.isBroker()) {
              await router.push('/insurance/applications')
            } else {
              await router.push('/')
            }
          }
        } catch (error) {
          // If it's our access denied error, rethrow it
          if (error.message && error.message.includes('Access denied')) {
            throw error
          }
          // For other errors (like API failures), clear auth and rethrow
          console.error('[Auth] Error during permission check:', error)
          delete axios.defaults.headers.common['Authorization']

          // Report to Sentry
          Sentry.captureException(error, {
            tags: { context: 'login_permissions_check' }
          })

          throw error
        }
      } catch (error) {
        // Report login errors to Sentry (network errors, timeouts, etc.)
        Sentry.captureException(error, {
          tags: { context: 'login_request' },
          extra: { username }
        })
        throw error
      }
    },
    async logout() {
      console.log('[Auth] Starting logout process')
      this.isLoggingOut = true
      try {
        console.log('[Auth] Calling logout endpoint')
        await axios.post(
          `${baseUrl}/logout`,
          {},
          {
            headers: {
              Authorization: `Bearer ${this.userToken}`
            },
            // Skip the 401 interceptor for logout calls
            skipAuthInterceptor: true
          }
        )
        console.log('[Auth] Logout endpoint succeeded')
      } catch (error) {
        // Ignore 401 errors during logout - user is already unauthorized
        if (error.response?.status !== 401) {
          console.error('[Auth] Logout error (non-401):', error)
        } else {
          console.log('[Auth] Logout got 401 (expected, ignoring)')
        }
      } finally {
        console.log('[Auth] Cleaning up tokens and redirecting')
        localStorage.removeItem('userToken')
        localStorage.removeItem('tokenExpiry')
        this.userToken = null
        this.tokenExpiry = null
        this.userPermissions = []

        // Clear axios default header
        delete axios.defaults.headers.common['Authorization']

        router.push('/login').finally(() => {
          this.isLoggingOut = false
        })
      }
    },
    isTokenExpired() {
      return Date.now() > this.tokenExpiry
    },
    extendToken() {
      return Date.now() > this.tokenExpiry - 55 * 60000 && Date.now() < this.tokenExpiry
    },
    async checkToken() {
      // Don't check token if user is not logged in
      if (!this.userToken || !this.tokenExpiry) {
        return
      }

      if (this.isTokenExpired()) {
        localStorage.removeItem('userToken')
        localStorage.removeItem('tokenExpiry')
        this.userToken = null
        this.tokenExpiry = null
        this.userPermissions = []

        // Force logout on token expiry
        if (!this.isLoggingOut) {
          await this.logout()
        }
      }
    }
  }
})
